/* eslint-disable @typescript-eslint/no-empty-function */
import {
  Component,
  computed,
  forwardRef,
  HostBinding,
  input,
  model,
  output,
  signal
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SliderMark } from '@umss/shared/utils';
import { UmssMarkerComponent } from '../marker/marker.component';
import { UmssTooltipDirective } from '../../directives/tooltip.directive';

/**
 * A premium, responsive horizontal slider component supporting single values,
 * range selection, and custom marks.
 *
 * @example
 * <umss-slider-horizontal
 *   [min]="0"
 *   [max]="100"
 *   [(value)]="currentValue"
 *   [marks]="[{value: 0, label: 'Min'}, {value: 100, label: 'Max'}]"
 * ></umss-slider-horizontal>
 */
@Component({
  selector: 'umss-slider-horizontal',
  standalone: true,
  imports: [UmssMarkerComponent, UmssTooltipDirective],
  templateUrl: './slider-horizontal.component.html',
  styleUrls: ['./slider-horizontal.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssSliderHorizontalComponent),
      multi: true
    }
  ]
})
export class UmssSliderHorizontalComponent implements ControlValueAccessor {
  @HostBinding('style.display') display = 'contents';

  /** Optional label to display above the slider. */
  label = input<string>();

  /** Size of the label typography. */
  labelSize = input<'Mini' | 'Small' | 'Regular'>('Small');

  /** Minimum slider value. Defaults to 0. */
  min = input<number>(0);

  /** Maximum slider value. Defaults to 100. */
  max = input<number>(100);

  /** Step increment for slider values. Defaults to 1. */
  step = input<number>(1);

  /** Custom labeled marks to display along the slider rail. */
  marks = input<SliderMark[]>([]);

  /** Whether to show the current value(s) in the header. Defaults to false. */
  showValue = input<boolean>(false);

  /** Whether the slider is in range mode. Defaults to false. */
  range = input<boolean>(false);

  /** Whether the slider is disabled. Defaults to false. */
  disabled = input<boolean>(false);

  /** Current value for single-value mode. Supports two-way binding. */
  value = model<number>(0);

  /** Start value for range mode. Supports two-way binding. */
  rangeStart = model<number>(0);

  /** End value for range mode. Supports two-way binding. */
  rangeEnd = model<number>(100);

  /** Emits when the single value changes. */
  valueChange = output<number>();

  /** Emits when the range selection changes. */
  rangeChange = output<{ start: number; end: number }>();

  /** Internal state to track which handle is being dragged in range mode. */
  private isDraggingStart = signal(false);
  private isDraggingEnd = signal(false);

  /** Internal component ID for accessibility. */
  protected readonly id = `umss-slider-${Math.random().toString(36).substring(2, 9)}`;

  /** Internal CVA disabled state. */
  private readonly _disabledState = signal(false);

  /** Effective disabled state. */
  protected readonly isDisabled = computed(
    () => this.disabled() || this._disabledState()
  );

  /** ControlValueAccessor implementation */
  private _onChange: (value: number | { start: number; end: number }) => void =
    () => {};
  private _onTouched: () => void = () => {};

  /**
   * Writes a new value to the component (from the parent form).
   * @param value The value to write.
   */
  writeValue(value: number | { start: number; end: number } | null): void {
    if (value === null) return;

    if (this.range() && typeof value === 'object') {
      this.rangeStart.set(this.clamp(value.start));
      this.rangeEnd.set(this.clamp(value.end));
    } else if (typeof value === 'number') {
      this.value.set(this.clamp(value));
    }
  }

  /**
   * Registers a callback for when the component value changes.
   * @param fn The callback function.
   */
  registerOnChange(
    fn: (value: number | { start: number; end: number }) => void
  ): void {
    this._onChange = fn;
  }

  /**
   * Registers a callback for when the component is touched.
   * @param fn The callback function.
   */
  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  /**
   * Sets the disabled state of the component.
   * @param isDisabled Whether the component should be disabled.
   */
  setDisabledState(isDisabled: boolean): void {
    this._disabledState.set(isDisabled);
  }

  /**
   * Handles input events for the single-value slider.
   * @param event The native input event.
   */
  onSliderChange(event: Event): void {
    if (this.isDisabled()) return;

    const target = event.target as HTMLInputElement;
    const val = parseFloat(target.value);
    this.value.set(val);
    this._onChange(val);
    this.valueChange.emit(val);
  }

  /**
   * Handles input events for the range slider.
   * @param event The native input event.
   */
  onRangeInputChange(event: Event): void {
    if (this.isDisabled()) return;

    const target = event.target as HTMLInputElement;
    const newValue = parseFloat(target.value);

    if (this.isDraggingStart()) {
      this.rangeStart.set(Math.min(newValue, this.rangeEnd()));
    } else if (this.isDraggingEnd()) {
      this.rangeEnd.set(Math.max(newValue, this.rangeStart()));
    } else {
      const distToStart = Math.abs(newValue - this.rangeStart());
      const distToEnd = Math.abs(newValue - this.rangeEnd());

      if (distToStart < distToEnd) {
        this.isDraggingStart.set(true);
        this.rangeStart.set(Math.min(newValue, this.rangeEnd()));
      } else {
        this.isDraggingEnd.set(true);
        this.rangeEnd.set(Math.max(newValue, this.rangeStart()));
      }
    }

    const result = { start: this.rangeStart(), end: this.rangeEnd() };
    this._onChange(result);
    this.rangeChange.emit(result);
  }

  /**
   * Resets dragging state for range handles.
   */
  onRangeInputMouseDown(): void {
    this.isDraggingStart.set(false);
    this.isDraggingEnd.set(false);
  }

  /**
   * Resets dragging state for range handles.
   */
  onRangeInputMouseUp(): void {
    this.isDraggingStart.set(false);
    this.isDraggingEnd.set(false);
  }

  /**
   * Handles mousedown on a marker to provide custom drag when the marker
   * overlays the native input (which would otherwise lose the drag event).
   * @param event The native mousedown event.
   * @param handle Which handle is being dragged.
   */
  onMarkerMouseDown(
    event: MouseEvent,
    handle: 'value' | 'start' | 'end' = 'value'
  ): void {
    event.preventDefault();
    if (this.isDisabled()) return;

    const rail = (event.currentTarget as HTMLElement)
      .closest('.slider-wrapper')
      ?.querySelector('.slider-rail');
    if (!rail) return;

    const onMove = (moveEvent: MouseEvent) => {
      const rect = rail.getBoundingClientRect();
      const dx = moveEvent.clientX - rect.left;
      const pct = Math.max(0, Math.min(1, dx / rect.width));
      const raw = this.min() + pct * (this.max() - this.min());
      const stepped = Math.round(raw / this.step()) * this.step();
      const clamped = Math.min(Math.max(stepped, this.min()), this.max());

      if (handle === 'value') {
        this.value.set(clamped);
        this._onChange(clamped);
        this.valueChange.emit(clamped);
      } else if (handle === 'start') {
        const newStart = Math.min(clamped, this.rangeEnd());
        this.rangeStart.set(newStart);
        const result = { start: newStart, end: this.rangeEnd() };
        this._onChange(result);
        this.rangeChange.emit(result);
      } else {
        const newEnd = Math.max(clamped, this.rangeStart());
        this.rangeEnd.set(newEnd);
        const result = { start: this.rangeStart(), end: newEnd };
        this._onChange(result);
        this.rangeChange.emit(result);
      }
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      this._onTouched();
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }

  /**
   * Clamps a numeric value between the component's min and max.
   * @param value The value to clamp.
   * @returns The clamped value.
   */
  private clamp(value: number): number {
    return Math.min(Math.max(value, this.min()), this.max());
  }

  /** The percentage position of the single-value thumb. */
  percentage = computed(() => {
    const factor = this.max() - this.min();
    if (factor <= 0) return 0;
    const p = ((this.value() - this.min()) / factor) * 100;
    return Math.max(0, Math.min(100, p));
  });

  /** The percentage position of the range start thumb. */
  rangeStartPercentage = computed(() => {
    const factor = this.max() - this.min();
    if (factor <= 0) return 0;
    const p = ((this.rangeStart() - this.min()) / factor) * 100;
    return Math.max(0, Math.min(100, p));
  });

  /** The percentage position of the range end thumb. */
  rangeEndPercentage = computed(() => {
    const factor = this.max() - this.min();
    if (factor <= 0) return 0;
    const p = ((this.rangeEnd() - this.min()) / factor) * 100;
    return Math.max(0, Math.min(100, p));
  });

  /**
   * Calculates the percentage position for a given value.
   * Used for positioning marks.
   * @param val The value to convert to a percentage.
   * @returns The percentage value (0-100).
   */
  getMarkPercentage(val: number): number {
    const factor = this.max() - this.min();
    if (factor <= 0) return 0;
    const p = ((val - this.min()) / factor) * 100;
    return Math.max(0, Math.min(100, p));
  }

  /**
   * TrackBy function for marks list.
   * @param index The index of the item.
   * @param mark The mark item.
   * @returns The unique identifier (value).
   */
  trackByValue(index: number, mark: SliderMark): number {
    return mark.value;
  }
}
